import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  Sparkles,
  Users,
  Plus,
  Search,
  RefreshCw,
  Trash2,
  ChevronRight,
  Loader2,
  Wand2,
} from "lucide-react";
import { formatDate } from "@ckgo2/shared/utils";
import toast from "react-hot-toast";

interface Segment {
  id: string;
  name: string;
  description?: string;
  aiPrompt: string;
  generatedSql: string;
  segmentType: "manual" | "automatic";
  customerCount: number;
  lastGeneratedAt?: string;
  isActive: boolean;
  createdAt: string;
}

interface Customer {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  totalSpent?: number;
  tripsCount?: number;
}

interface SegmentPreview {
  generatedSql: string;
  description: string;
  customers: Customer[];
  customerCount: number;
}

export default function SegmentsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);

  // Fetch segments
  const { data: segmentsData, isLoading } = useQuery({
    queryKey: ["segments"],
    queryFn: () => api.get<ApiResponse<Segment[]>>("/api/admin/segments"),
  });

  const segments = segmentsData?.data || [];

  // Filter segments
  const filteredSegments = segments.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.aiPrompt.toLowerCase().includes(search.toLowerCase())
  );

  // Delete segment
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/segments/${id}`),
    onSuccess: () => {
      toast.success("Segment smazán");
      queryClient.invalidateQueries({ queryKey: ["segments"] });
    },
    onError: () => {
      toast.error("Nepodařilo se smazat segment");
    },
  });

  // Regenerate segment
  const regenerateMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<ApiResponse<Segment>>(`/api/admin/segments/${id}/regenerate`),
    onSuccess: () => {
      toast.success("Segment přegenerován");
      queryClient.invalidateQueries({ queryKey: ["segments"] });
    },
    onError: () => {
      toast.error("Nepodařilo se přegenerovat segment");
    },
  });

  const handleDelete = (id: string) => {
    if (window.confirm("Opravdu chcete smazat tento segment?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            AI Segmenty
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Vytvářejte zákaznické segmenty pomocí přirozeného jazyka
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <Wand2 className="h-5 w-5" />
          Nový segment
        </button>
      </div>

      {/* Info box */}
      <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <div>
            <h3 className="font-medium text-primary-900 dark:text-primary-100">
              Jak to funguje?
            </h3>
            <p className="mt-1 text-sm text-primary-700 dark:text-primary-300">
              Napište popis segmentu v přirozeném jazyce (např. "zákazníci, kteří s námi byli loni na Bali")
              a AI vytvoří segment automaticky. Při opakovaném použití segmentu se zákazníci přegenerují.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Hledat segmenty..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Segments list */}
      <div className="card">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : filteredSegments.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center text-gray-500">
            <Users className="mb-2 h-8 w-8 text-gray-300" />
            <p>Žádné segmenty</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-2 text-sm text-primary-600 hover:underline"
            >
              Vytvořit první segment
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredSegments.map((segment) => (
              <div
                key={segment.id}
                className="flex items-center justify-between py-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {segment.name}
                    </h3>
                    <span className="badge bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                      {segment.customerCount} zákazníků
                    </span>
                    {segment.segmentType === "automatic" && (
                      <span className="badge bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400">
                        Automatický
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {segment.aiPrompt}
                  </p>
                  {segment.lastGeneratedAt && (
                    <p className="mt-1 text-xs text-gray-500">
                      Poslední aktualizace: {formatDate(segment.lastGeneratedAt)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => regenerateMutation.mutate(segment.id)}
                    disabled={regenerateMutation.isPending}
                    className="btn-ghost btn-sm"
                    title="Přegenerovat zákazníky"
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${regenerateMutation.isPending ? "animate-spin" : ""}`}
                    />
                  </button>
                  <button
                    onClick={() => setSelectedSegment(segment)}
                    className="btn-ghost btn-sm"
                    title="Zobrazit zákazníky"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(segment.id)}
                    className="btn-ghost btn-sm text-error-600 hover:bg-error-50"
                    title="Smazat segment"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateSegmentModal onClose={() => setShowCreateModal(false)} />
      )}

      {/* Segment Detail Modal */}
      {selectedSegment && (
        <SegmentDetailModal
          segment={selectedSegment}
          onClose={() => setSelectedSegment(null)}
        />
      )}
    </div>
  );
}

// Create Segment Modal
function CreateSegmentModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [preview, setPreview] = useState<SegmentPreview | null>(null);

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: (promptText: string) =>
      api.post<ApiResponse<SegmentPreview>>("/api/admin/segments/preview", {
        prompt: promptText,
      }),
    onSuccess: (data) => {
      setPreview(data.data || null);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se vytvořit náhled");
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: { name: string; prompt: string }) =>
      api.post<ApiResponse<Segment>>("/api/admin/segments", data),
    onSuccess: () => {
      toast.success("Segment vytvořen");
      queryClient.invalidateQueries({ queryKey: ["segments"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se vytvořit segment");
    },
  });

  const handlePreview = () => {
    if (!prompt.trim()) {
      toast.error("Zadejte popis segmentu");
      return;
    }
    previewMutation.mutate(prompt);
  };

  const handleCreate = () => {
    if (!name.trim()) {
      toast.error("Zadejte název segmentu");
      return;
    }
    if (!prompt.trim()) {
      toast.error("Zadejte popis segmentu");
      return;
    }
    createMutation.mutate({ name, prompt });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Nový segment
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Popište segment v přirozeném jazyce
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <label className="label">Název segmentu</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="např. VIP zákazníci"
              className="input"
            />
          </div>

          <div>
            <label className="label">Popis segmentu (AI prompt)</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="např. zákazníci, kteří s námi byli loni na Bali a utratili více než 50000 Kč"
              rows={3}
              className="input"
            />
            <p className="mt-1 text-xs text-gray-500">
              Tipy: "zákazníci bez objednávky tento rok", "zákazníci s narozeninami tento měsíc",
              "zákazníci z Prahy kteří preferují Asii"
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handlePreview}
              disabled={previewMutation.isPending}
              className="btn-ghost flex-1"
            >
              {previewMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Search className="h-5 w-5" />
              )}
              Náhled
            </button>
            <button
              onClick={handleCreate}
              disabled={createMutation.isPending || !preview}
              className="btn-primary flex-1"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Plus className="h-5 w-5" />
              )}
              Vytvořit segment
            </button>
          </div>

          {/* Preview results */}
          {preview && (
            <div className="mt-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900 dark:text-white">
                  Náhled segmentu
                </h3>
                <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                  {preview.customerCount} zákazníků
                </span>
              </div>
              {preview.description && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {preview.description}
                </p>
              )}
              {preview.customers.length > 0 && (
                <div className="mt-3 max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="pb-2 text-left font-medium text-gray-500">
                          Email
                        </th>
                        <th className="pb-2 text-left font-medium text-gray-500">
                          Jméno
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {preview.customers.slice(0, 10).map((customer) => (
                        <tr key={customer.id}>
                          <td className="py-2 text-gray-900 dark:text-white">
                            {customer.email}
                          </td>
                          <td className="py-2 text-gray-600 dark:text-gray-400">
                            {customer.firstName} {customer.lastName}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.customers.length > 10 && (
                    <p className="mt-2 text-center text-xs text-gray-500">
                      ... a dalších {preview.customers.length - 10} zákazníků
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">
            Zrušit
          </button>
        </div>
      </div>
    </div>
  );
}

// Segment Detail Modal
function SegmentDetailModal({
  segment,
  onClose,
}: {
  segment: Segment;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["segment", segment.id],
    queryFn: () =>
      api.get<ApiResponse<{ segment: Segment; customers: Customer[]; total: number }>>(
        `/api/admin/segments/${segment.id}/customers`
      ),
  });

  const customers = data?.data?.customers || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {segment.name}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {segment.aiPrompt}
            </p>
          </div>
          <span className="badge bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
            {segment.customerCount} zákazníků
          </span>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : customers.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-gray-500">
              Žádní zákazníci v segmentu
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-3 text-left text-sm font-medium text-gray-500">
                    Email
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-500">
                    Jméno
                  </th>
                  <th className="pb-3 text-right text-sm font-medium text-gray-500">
                    Útrata
                  </th>
                  <th className="pb-3 text-right text-sm font-medium text-gray-500">
                    Zájezdů
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {customers.map((customer) => (
                  <tr key={customer.id}>
                    <td className="py-3 text-gray-900 dark:text-white">
                      {customer.email}
                    </td>
                    <td className="py-3 text-gray-600 dark:text-gray-400">
                      {customer.firstName} {customer.lastName}
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                      {(customer.totalSpent || 0).toLocaleString("cs-CZ")} Kč
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                      {customer.tripsCount || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-ghost">
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}
